import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus, Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { PriceDisplay } from "@/components/PriceDisplay";

const COLOR_HEX = {
  Black: "#111111", White: "#F5F5F0", Gold: "#C4A24A", Silver: "#C0C0C0", Beige: "#D8C5A5",
  Brown: "#6B4A2B", Red: "#8E1B1B", Blue: "#1E3A5F", Green: "#2F4F3E", Pink: "#E6B7C1",
};

export default function ProductCard({ product, index = 0 }) {
  const { currency, addToCart, inCart, toggleWishlist, inWishlist, t, lang } = useApp();
  const added = inCart(product.id);
  const liked = inWishlist(product.id);
  const subtitle = lang === "ru" ? product.subtitle_ru : product.subtitle_en;
  const stock = product.stock ?? 0;
  const colors = product.colors || [];
  const sizes = product.sizes || [];
  const sizeStock = product.size_stock || {};
  const tracksSizes = Object.keys(sizeStock).length > 0;
  const hasSizes = sizes.length > 0;
  // Auto sold-out: a size-tracked product is sold out once every size is gone,
  // even if the total-stock field hasn't caught up.
  const allSizesGone = tracksSizes && sizes.length > 0 && sizes.every((s) => (sizeStock[s] || 0) <= 0);
  const soldOut = stock <= 0 || allSizesGone;
  const lowStock = !soldOut && stock > 0 && stock <= 3;

  const [selectedSize, setSelectedSize] = useState(null);

  const sizeAvailable = (s) => (tracksSizes ? (sizeStock[s] || 0) > 0 : stock > 0);
  const availableCount = tracksSizes ? sizes.filter((s) => (sizeStock[s] || 0) > 0).length : sizes.length;
  const secondImage = (product.images && product.images.length > 1) ? product.images[1] : null;
  const onSale = product.original_price_rub > 0 && product.original_price_rub > product.price_rub;
  const discountPct = onSale ? Math.round((1 - product.price_rub / product.original_price_rub) * 100) : 0;
  const altText = `${product.brand ? product.brand + " " : ""}${product.name} – BuyHeda`;

  const handleAdd = () => {
    if (hasSizes && !selectedSize) {
      toast.error(t.shop.chooseSize);
      return;
    }
    addToCart(product, hasSizes ? selectedSize : null);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: (index % 4) * 0.06, ease: [0.19, 1, 0.22, 1] }}
      className="group relative flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-surface aspect-[3/4]">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} className="block h-full w-full">
          <img
            src={product.image}
            alt={altText}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06] ${soldOut ? "opacity-70 grayscale-[0.35]" : ""}`}
          />
          {secondImage && (
            <img
              src={secondImage}
              alt={altText}
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            />
          )}
          <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/5 transition-colors duration-500" />
        </Link>

        <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
          {soldOut ? (
            <span className="bg-ink text-paper text-[10px] uppercase tracking-[0.18em] px-2.5 py-1" data-testid={`badge-soldout-${product.id}`}>
              {t.shop.soldOut}
            </span>
          ) : (
            <>
              {onSale && discountPct > 0 && (
                <span className="bg-red-600 text-white text-[10px] font-medium uppercase tracking-[0.14em] px-2.5 py-1" data-testid={`badge-sale-${product.id}`}>
                  -{discountPct}%
                </span>
              )}
              {lowStock && (
                <span className="bg-paper text-ink text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border border-line" data-testid={`badge-lowstock-${product.id}`}>
                  {t.shop.lowStock}
                </span>
              )}
            </>
          )}
        </div>

        <button
          onClick={() => toggleWishlist(product)}
          data-testid={`wishlist-toggle-${product.id}`}
          aria-label="Save to favourites"
          className={`absolute top-3 right-3 h-10 w-10 flex items-center justify-center backdrop-blur-sm transition-all duration-500 ${
            liked ? "bg-ink text-paper" : "bg-paper/70 text-ink opacity-0 group-hover:opacity-100 hover:bg-paper"
          }`}
        >
          <Heart className="h-4 w-4" strokeWidth={1.4} fill={liked ? "currentColor" : "none"} />
        </button>

        {soldOut ? (
          <div
            className="absolute bottom-0 left-0 right-0 flex items-center justify-center py-4 text-xs uppercase tracking-[0.2em] bg-ink/85 text-paper"
            data-testid={`soldout-${product.id}`}
          >
            {t.shop.soldOut}
          </div>
        ) : (
          <button
            onClick={handleAdd}
            data-testid={`add-to-cart-${product.id}`}
            className={`absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 py-4 text-xs uppercase tracking-[0.2em] transition-all duration-500 ${
              added ? "bg-accent text-ink translate-y-0" : "bg-ink text-paper translate-y-full group-hover:translate-y-0"
            }`}
          >
            {added ? <><Check className="h-3.5 w-3.5" /> {t.shop.added}</> : <><Plus className="h-3.5 w-3.5" /> {t.shop.add}</>}
          </button>
        )}
      </div>

      <div className="pt-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">{product.brand}</p>
          <h3 className="font-display text-2xl leading-none">{product.name}</h3>
          <p className="hidden sm:block text-xs text-ink/50 mt-2 font-light">{subtitle}</p>
          {colors.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3" data-testid={`colors-${product.id}`}>
              {colors.map((c) => (
                <span
                  key={c}
                  title={c}
                  className="h-3.5 w-3.5 rounded-full border border-black/15"
                  style={{ backgroundColor: COLOR_HEX[c] || "#cccccc" }}
                />
              ))}
            </div>
          )}
          {hasSizes && !soldOut && (
            <div className="mt-3" data-testid={`size-picker-${product.id}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted mb-1.5" data-testid={`sizes-available-${product.id}`}>
                {product.category === "shoes" ? `${t.shop.selectSize} (EU)` : t.shop.selectSize}
                <span className="text-ink/40"> · {availableCount} {t.shop.available}</span>
              </p>
              <div className="flex flex-wrap gap-1.5">
                {sizes.map((s) => {
                  const avail = sizeAvailable(s);
                  const active = selectedSize === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={!avail}
                      title={avail ? s : t.shop.soldOut}
                      onClick={() => setSelectedSize(active ? null : s)}
                      data-testid={`size-option-${product.id}-${s.toLowerCase()}`}
                      className={`min-w-[2.25rem] px-2 py-1 text-[11px] uppercase tracking-[0.08em] border transition-colors ${
                        active
                          ? "border-ink bg-ink text-paper"
                          : avail
                          ? "border-line text-ink hover:border-ink"
                          : "border-line text-ink/25 line-through cursor-not-allowed"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <p className="font-sans text-sm whitespace-nowrap pt-1">
          <PriceDisplay priceRub={product.price_rub} originalRub={product.original_price_rub} currency={currency} testId={`price-${product.id}`} />
        </p>
      </div>
    </motion.article>
  );
}
